// @ts-check

import { computed, toValue } from "vue";
import { isPresent } from "#ce/utils/props";

/**
 * Resolves a component's presence-based disabled state and the derived
 * `aria-disabled` attribute value, so components do not re-implement the same
 * `computed(() => toBoolean(props.disabled))` + aria derivation.
 *
 * Presence-based (via {@link isPresent}): a bare `disabled` / `disabled=""` is on,
 * `disabled="false"` is off — consistent with the registration wrapper's
 * normalization and with {@link import("#ce/composables/useFormControl").useFormControl}.
 *
 * For form controls that must also respond to an ancestor `<fieldset disabled>`,
 * use `useFormControl` instead — it layers the fieldset state on top of this.
 *
 * @param {import("vue").MaybeRefOrGetter<*>} disabled - the disabled source (prop ref/getter)
 * @returns {{ disabled: import("vue").ComputedRef<boolean>, ariaDisabled: import("vue").ComputedRef<"true" | undefined> }}
 */
export function useDisabled(disabled) {
  const isDisabled = computed(() => isPresent(toValue(disabled)));

  const ariaDisabled = computed(() => {
    if (isDisabled.value) {
      return "true";
    }

    return undefined;
  });

  return {
    disabled: isDisabled,
    ariaDisabled,
  };
}
